import { Checkbox, Modal } from "antd";
import React, { useCallback, useState } from "react";
import { useNotification } from "../../contexts/Notifications";
import { ReplicateSpaceOptionsInputType } from "../../generated/types";
import { Space } from "../../types";
import { DisplayTreeNode } from "../DisplayTree/types";
import { InputRow } from "../FloorplanEditor/ImportFloorplanModal/SetPointRow";
import { SpacesTree } from "../SpacesTree/SpacesTree";
import { isNodeSpace } from "../SpatialConfigurationMode/utils";
import styles from "./ReplicateSpaceModal.module.css";

const SPACE_LABEL = "Space";
const PARENT_SPACE_LABEL = "New Parent Space";
const FLOOR_HEIGHT = "Floor Height";
const NEW_SPACE_NAME = "New Space Name";

export type SpaceWithName = Pick<Space, "id" | "name">;

// Every flag is resolved locally, so these are plain booleans rather than the
// nullable generated input type.
type ReplicateOptions = {
  [K in keyof ReplicateSpaceOptionsInputType]-?: boolean;
};

export const DEFAULT_REPLICATE_OPTIONS: ReplicateOptions = {
  aliasScenesToSource: true,
  copyMappings: true,
  copyProgress: true,
  copySpotlights: false,
  copyDescendantFloorplans: true,
  copySpaceRecords: true,
};

type OptionKey = keyof ReplicateOptions;

const OPTIONS: {
  key: OptionKey;
  label: string;
  note?: string;
  warning?: string;
}[] = [
  {
    key: "copyMappings",
    label: "Copy work mappings",
    note: "Links the new spaces to the same components as the original.",
  },
  {
    key: "copyProgress",
    label: "Copy progress history",
    warning:
      "The copy will report the same completion as the original, so progress totals above it count that work twice until you correct them.",
  },
  {
    key: "copySpotlights",
    label: "Copy spotlights",
    warning:
      "Creates duplicate issues, which will show up in reports and Aconex.",
  },
  {
    key: "copyDescendantFloorplans",
    label: "Copy floorplans below the selected space",
    note: "Otherwise only the selected space's own floorplan is copied.",
  },
  {
    key: "copySpaceRecords",
    label: "Copy BIM models, statuses and comments",
  },
  {
    key: "aliasScenesToSource",
    label: "Alias new scenes to the originals",
    note: "The copy shows the original's 360s until it is scanned. Needs a new dump sheet to reach shell360. Scanning sequences are not copied.",
  },
];

interface ReplicateSpaceModalProps {
  space: SpaceWithName;
  // Resolves once the mutation settles; the editor closes the modal then, so it
  // stays up with a spinner while the copy is in flight.
  onCreate: (
    spaceId: string,
    parentSpaceId: string,
    options: ReplicateSpaceOptionsInputType,
    newSpaceName?: string,
    newFloorHeight?: number,
  ) => Promise<void>;
  onClose: () => void;
}

enum ReplicateStep {
  SelectParent,
  SetNameAndHeight,
  SelectOptions,
}

type ReplicateStepToComponent = Record<ReplicateStep, JSX.Element>;

export const ReplicateSpaceModal = (props: ReplicateSpaceModalProps) => {
  const { space, onCreate, onClose } = props;
  const notify = useNotification();
  const [parentSpace, setParentSpace] = useState<SpaceWithName>();
  // Left undefined unless the user sets it, so the copy keeps the source's
  // heights. Sending 0 would overwrite them.
  const [floorHeight, setFloorHeight] = useState<number>();
  const [newSpaceName, setNewSpaceName] = useState<string>();
  const [options, setOptions] = useState(DEFAULT_REPLICATE_OPTIONS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replicateStep, setReplicateStep] = useState<ReplicateStep>(
    ReplicateStep.SelectParent,
  );

  const selectParentSpace = (node: DisplayTreeNode) => {
    isNodeSpace(node)
      ? setParentSpace({ id: node.key, name: node.name })
      : notify("Select a space node", "warning");
  };

  const setFloorHeightIfValid = (value: number) =>
    setFloorHeight(Number.isFinite(value) ? value : undefined);

  // Copying mappings and progress can take a while, so the dialog stays up with
  // a spinner until the mutation settles rather than vanishing on click.
  const replicateSpaceCallback = useCallback(
    async (newParentId: string) => {
      setIsSubmitting(true);
      try {
        await onCreate(
          space.id,
          newParentId,
          options,
          newSpaceName,
          floorHeight,
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [onCreate, space.id, options, newSpaceName, floorHeight],
  );

  const steps: ReplicateStepToComponent = {
    [ReplicateStep.SelectParent]: (
      <React.Fragment>
        <div className={styles["space-name-box"]}>
          {`${SPACE_LABEL}: ${space.name}`}
        </div>
        <div className={styles["space-name-box"]}>
          {`${PARENT_SPACE_LABEL}: ${parentSpace?.name ?? ""}`}
        </div>
        <div className={styles["spaces-tree-box"]}>
          <SpacesTree onSelect={selectParentSpace} />
        </div>
      </React.Fragment>
    ),
    [ReplicateStep.SetNameAndHeight]: (
      <React.Fragment>
        <InputRow
          name={NEW_SPACE_NAME}
          value={newSpaceName}
          placeholder="Enter a name for the new space..."
          onSet={setNewSpaceName}
          type="string"
        />
        <InputRow
          name={FLOOR_HEIGHT}
          value={floorHeight}
          placeholder="Leave empty to keep the original height"
          onSet={setFloorHeightIfValid}
          unit={"mm"}
        />
      </React.Fragment>
    ),
    [ReplicateStep.SelectOptions]: (
      <React.Fragment>
        {OPTIONS.map(({ key, label, note, warning }) => (
          <div key={key} className={styles["option-row"]}>
            <Checkbox
              checked={options[key]}
              onChange={(e) =>
                setOptions((current) => ({
                  ...current,
                  [key]: e.target.checked,
                }))
              }
            >
              {label}
            </Checkbox>
            {note && <div className={styles["option-note"]}>{note}</div>}
            {warning && options[key] && (
              <div className={styles["option-warning"]}>{warning}</div>
            )}
          </div>
        ))}
      </React.Fragment>
    ),
  };

  const onOk = useCallback(() => {
    if (parentSpace === undefined) {
      notify("Select a parent space", "warning");
      return;
    }
    if (replicateStep === ReplicateStep.SelectParent) {
      setReplicateStep(ReplicateStep.SetNameAndHeight);
      return;
    }
    if (replicateStep === ReplicateStep.SetNameAndHeight) {
      setReplicateStep(ReplicateStep.SelectOptions);
      return;
    }
    replicateSpaceCallback(parentSpace.id);
  }, [replicateStep, replicateSpaceCallback, parentSpace, notify]);

  return (
    <Modal
      bodyStyle={{
        height:
          replicateStep === ReplicateStep.SelectParent
            ? "80vh"
            : replicateStep === ReplicateStep.SelectOptions
            ? "45vh"
            : "30vh",
      }}
      closable={false}
      visible={true}
      // The mask and Escape both default to closing, and destroyOnClose would
      // unmount this while the copy is still running - so every route out is
      // blocked while it is, not just the Cancel button.
      onCancel={() => !isSubmitting && onClose()}
      maskClosable={!isSubmitting}
      keyboard={!isSubmitting}
      destroyOnClose={true}
      onOk={onOk}
      confirmLoading={isSubmitting}
      okButtonProps={{ disabled: !parentSpace?.id }}
      cancelButtonProps={{ disabled: isSubmitting }}
      okText={replicateStep === ReplicateStep.SelectOptions ? "Submit" : "Next"}
    >
      {steps[replicateStep]}
    </Modal>
  );
};
